import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { 
  MapPin, 
  Clock, 
  DollarSign, 
  Users, 
  Code, 
  Shield, 
  Zap, 
  Globe,
  Heart,
  Coffee,
  Briefcase,
  GraduationCap,
  ArrowRight,
  Building,
  Wifi,
  Target
} from 'lucide-react';

const CareersPage = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const jobOpenings = [
    {
      id: 1,
      title: "Senior Backend Engineer",
      department: "engineering",
      location: "Remote",
      type: "Full-time",
      salary: "$140k - $180k",
      posted: "2 days ago",
      description: "Build and scale our proxy infrastructure that serves millions of requests daily. Work with high-performance systems, distributed architecture, and cutting-edge networking technologies.",
      requirements: [
        "5+ years of backend development experience",
        "Strong experience with Python, Go, or Rust",
        "Experience with distributed systems and microservices",
        "Knowledge of networking protocols (HTTP/HTTPS, SOCKS5)",
        "Experience with cloud platforms (AWS, GCP, Azure)",
        "Understanding of proxy technologies and VPN systems"
      ],
      niceToHave: [
        "Experience with proxy/VPN infrastructure",
        "Knowledge of network security and encryption",
        "Experience with Kubernetes and Docker",
        "Background in telecommunications or ISP industry"
      ]
    },
    {
      id: 2,
      title: "DevOps Engineer",
      department: "engineering",
      location: "Remote / New York",
      type: "Full-time", 
      salary: "$120k - $160k",
      posted: "1 week ago",
      description: "Manage and optimize our global proxy network infrastructure. Ensure 99.9% uptime across 200+ countries and handle massive scale deployments.",
      requirements: [
        "4+ years of DevOps/Infrastructure experience",
        "Strong experience with AWS, GCP, or Azure",
        "Proficiency in Infrastructure as Code (Terraform, CloudFormation)",
        "Experience with container orchestration (Kubernetes, Docker)",
        "Knowledge of monitoring and logging systems",
        "Scripting skills in Python, Bash, or Go"
      ],
      niceToHave: [
        "Experience with CDN and edge computing",
        "Network administration background",
        "Experience with high-availability systems",
        "Knowledge of security best practices"
      ]
    },
    {
      id: 3,
      title: "Network Engineer",
      department: "engineering", 
      location: "Remote / London",
      type: "Full-time",
      salary: "$110k - $145k",
      posted: "3 days ago",
      description: "Design and maintain our global proxy network. Work with ISPs, data centers, and mobile carriers to ensure optimal performance and coverage.",
      requirements: [
        "Bachelor's degree in Network Engineering or related field",
        "3+ years of network engineering experience",
        "Strong knowledge of TCP/IP, BGP, OSPF",
        "Experience with network monitoring and troubleshooting",
        "Understanding of proxy protocols and load balancing",
        "Cisco, Juniper, or similar networking certifications preferred"
      ],
      niceToHave: [
        "Experience with mobile carrier networks",
        "Knowledge of CDN technologies",
        "ISP or telecommunications background",
        "Experience with DDoS mitigation"
      ]
    },
    {
      id: 4,
      title: "Enterprise Sales Manager",
      department: "sales",
      location: "Remote",
      type: "Full-time",
      salary: "$90k - $130k + Commission",
      posted: "5 days ago",
      description: "Drive enterprise client acquisition and manage relationships with Fortune 500 companies. Help businesses integrate our proxy solutions into their operations.",
      requirements: [
        "5+ years of B2B enterprise sales experience",
        "Track record of closing deals $50k+ annually",
        "Experience selling technical solutions to IT teams",
        "Strong presentation and communication skills",
        "Understanding of networking and infrastructure solutions",
        "CRM experience (Salesforce, HubSpot)"
      ],
      niceToHave: [
        "Experience selling proxy, VPN, or networking solutions",
        "Technical background or engineering degree",
        "Experience with subscription/SaaS sales models",
        "Existing network in enterprise IT space"
      ]
    },
    {
      id: 5,
      title: "Customer Success Manager",
      department: "customer-success",
      location: "Remote / Austin",
      type: "Full-time",
      salary: "$70k - $95k",
      posted: "1 week ago",
      description: "Ensure customer satisfaction and drive product adoption. Help clients optimize their proxy usage and achieve their business objectives.",
      requirements: [
        "3+ years of customer success or account management experience",
        "Strong technical aptitude and problem-solving skills",
        "Experience with SaaS or technical products",
        "Excellent communication and interpersonal skills",
        "Data-driven approach to customer success",
        "Experience with customer success platforms"
      ],
      niceToHave: [
        "Technical background in networking or web technologies",
        "Experience with proxy or web scraping tools",
        "Customer success certifications",
        "Multilingual capabilities"
      ]
    },
    {
      id: 6,
      title: "Security Engineer",
      department: "engineering",
      location: "Remote / Boston",
      type: "Full-time",
      salary: "$130k - $170k",
      posted: "4 days ago",
      description: "Protect our infrastructure and customer data. Implement security best practices across our global proxy network and ensure compliance with industry standards.",
      requirements: [
        "4+ years of cybersecurity experience",
        "Strong knowledge of network security protocols",
        "Experience with security frameworks (SOC2, ISO 27001)",
        "Proficiency in security tools and technologies",
        "Understanding of encryption and authentication systems",
        "Experience with penetration testing and vulnerability assessments"
      ],
      niceToHave: [
        "CISSP, CISM, or similar security certifications",
        "Experience with proxy and VPN security",
        "Background in network infrastructure security",
        "Experience with compliance audits"
      ]
    },
    {
      id: 7,
      title: "Product Manager",
      department: "product",
      location: "Remote", 
      type: "Full-time",
      salary: "$120k - $155k",
      posted: "6 days ago",
      description: "Drive product strategy and roadmap for our proxy platform. Work with engineering, sales, and customers to deliver features that solve real business problems.",
      requirements: [
        "4+ years of product management experience",
        "Strong analytical and strategic thinking skills",
        "Experience with technical B2B products",
        "Understanding of API design and developer tools",
        "Data-driven decision making experience",
        "Excellent stakeholder management skills"
      ],
      niceToHave: [
        "Technical background or engineering degree",
        "Experience with networking or infrastructure products",
        "Experience with developer-focused products",
        "Background in SaaS or enterprise software"
      ]
    },
    {
      id: 8,
      title: "Marketing Manager",
      department: "marketing",
      location: "Remote / New York",
      type: "Full-time",
      salary: "$85k - $115k", 
      posted: "1 week ago",
      description: "Lead our digital marketing efforts and build brand awareness in the developer and enterprise communities. Drive lead generation and customer acquisition.",
      requirements: [
        "4+ years of B2B marketing experience",
        "Strong content marketing and SEO skills",
        "Experience with marketing automation platforms",
        "Analytics and data-driven marketing approach",
        "Understanding of technical/developer audiences",
        "Experience with paid advertising (Google, LinkedIn)"
      ],
      niceToHave: [
        "Experience marketing to developers or technical audiences",
        "Background in SaaS or infrastructure products",
        "Experience with developer community building",
        "Technical writing or content creation skills"
      ]
    }
  ];

  const departments = [
    { key: 'all', name: 'All Departments', count: jobOpenings.length },
    { key: 'engineering', name: 'Engineering', count: jobOpenings.filter(job => job.department === 'engineering').length },
    { key: 'sales', name: 'Sales', count: jobOpenings.filter(job => job.department === 'sales').length },
    { key: 'customer-success', name: 'Customer Success', count: jobOpenings.filter(job => job.department === 'customer-success').length },
    { key: 'product', name: 'Product', count: jobOpenings.filter(job => job.department === 'product').length },
    { key: 'marketing', name: 'Marketing', count: jobOpenings.filter(job => job.department === 'marketing').length }
  ];

  const benefits = [
    {
      icon: Heart,
      title: "Health & Wellness",
      description: "Comprehensive health, dental, and vision insurance. Mental health support and wellness stipend."
    },
    {
      icon: Coffee,
      title: "Work-Life Balance", 
      description: "Flexible hours, unlimited PTO, and remote-first culture. Work from anywhere in the world."
    },
    {
      icon: GraduationCap,
      title: "Learning & Growth",
      description: "$2,000 annual learning budget, conference attendance, and mentorship programs."
    },
    {
      icon: DollarSign,
      title: "Competitive Compensation",
      description: "Top-tier salaries, equity participation, and performance bonuses. Annual compensation reviews."
    },
    {
      icon: Wifi,
      title: "Remote First",
      description: "Work from anywhere with a $1,000 home office setup budget and co-working space allowance."
    },
    {
      icon: Users,
      title: "Great Team",
      description: "Collaborative culture, team retreats, and opportunities to work with industry experts."
    }
  ];

  const filteredJobs = selectedDepartment === 'all' 
    ? jobOpenings 
    : jobOpenings.filter(job => job.department === selectedDepartment);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center mb-6">
              <Briefcase className="w-8 h-8 text-cyan-400 mr-3" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                Join Our <span className="text-cyan-400">Team</span>
              </h1>
            </div>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
              Help us build the world's most reliable proxy infrastructure. Work with cutting-edge technology 
              and solve complex networking challenges at global scale.
            </p>
            
            {/* Company Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-400 mb-2">50+</div>
                <div className="text-slate-400">Team Members</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-400 mb-2">200+</div>
                <div className="text-slate-400">Countries</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-400 mb-2">99.9%</div>
                <div className="text-slate-400">Uptime</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-400 mb-2">10K+</div>
                <div className="text-slate-400">Customers</div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Why Work at <span className="text-cyan-400">IP9</span>?
              </h2>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                We're building the future of internet infrastructure. Join us and make an impact while enjoying great benefits.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="bg-slate-950/50 border-slate-800 hover:border-slate-700 transition-all duration-300">
                  <CardHeader className="pb-4">
                    <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center mb-4">
                      <benefit.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">{benefit.title}</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-20 bg-slate-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Open <span className="text-cyan-400">Positions</span>
              </h2>
              <p className="text-xl text-slate-400">
                Find your next career opportunity and help shape the future of internet infrastructure.
              </p>
            </div>

            {/* Department Filter */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {departments.map((dept) => (
                <Button
                  key={dept.key}
                  variant={selectedDepartment === dept.key ? 'default' : 'outline'}
                  onClick={() => setSelectedDepartment(dept.key)}
                  className={selectedDepartment === dept.key 
                    ? 'bg-cyan-500 hover:bg-cyan-600' 
                    : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
                >
                  {dept.name} ({dept.count})
                </Button>
              ))}
            </div>

            {/* Job Listings */}
            <div className="space-y-6">
              {filteredJobs.map((job) => (
                <Card key={job.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all duration-300">
                  <CardHeader>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                      <div className="mb-4 lg:mb-0">
                        <h3 className="text-2xl font-bold text-white mb-2">{job.title}</h3>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 mr-1" />
                            <span className="capitalize">{job.department.replace('-', ' ')}</span>
                          </div>
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span>{job.location}</span>
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            <span>{job.type}</span>
                          </div>
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            <span>{job.salary}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <Badge variant="outline" className="border-slate-600 text-slate-400">
                          {job.posted}
                        </Badge>
                        <Button 
                          className="bg-cyan-500 hover:bg-cyan-400 text-slate-950"
                          onClick={() => window.location.href = 'mailto:support@ip9.io?subject=Job Application: ' + encodeURIComponent(job.title)}
                        >
                          Apply Now
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300 mb-6">{job.description}</p>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-white mb-3">Requirements:</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((req, index) => (
                            <li key={index} className="text-slate-400 text-sm flex items-start">
                              <Target className="w-3 h-3 mr-2 mt-1 text-cyan-400 flex-shrink-0" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-white mb-3">Nice to Have:</h4>
                        <ul className="space-y-2">
                          {job.niceToHave.map((item, index) => (
                            <li key={index} className="text-slate-400 text-sm flex items-start">
                              <Target className="w-3 h-3 mr-2 mt-1 text-green-400 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredJobs.length === 0 && (
              <div className="text-center py-16">
                <Briefcase className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No positions found</h3>
                <p className="text-slate-400">Try adjusting your department filter or check back later for new openings.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-3xl p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Don't See Your Role?
              </h2>
              <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                We're always looking for talented individuals to join our team. Send us your resume and tell us how you can contribute to IP9's mission.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Button 
                  size="lg" 
                  className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-4 text-lg"
                  onClick={() => window.location.href = 'mailto:support@ip9.io?subject=General Application - Resume'}
                >
                  Send Resume
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-4 text-lg"
                  onClick={() => window.location.href = 'mailto:support@ip9.io?subject=HR Team Inquiry'}
                >
                  Contact HR Team
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CareersPage;